import { useMemo, useState } from "react";
import { ChevronDown, Mail, MessageSquare, Search } from "lucide-react";
import { useT } from "@/i18n";
import { Button, Card, SectionHeading } from "@/components/ui";
import { cn } from "@/lib/cn";

type FAQ = { q: string; a: string; tag: string };

const FAQ_ES: FAQ[] = [
  {
    q: "¿Cuánto tarda la solicitud?",
    a: "No ofrecemos un plazo fijo. Depende de que el expediente esté completo y de la evaluación de la entidad de certificación.",
    tag: "Tiempos",
  },
  {
    q: "¿Qué documentos necesito?",
    a: "Para persona natural: la cédula es obligatoria y el RUT es opcional. Para persona jurídica: Cámara de Comercio, cédula del representante legal y RUT de la empresa.",
    tag: "Documentos",
  },
  {
    q: "¿Quién emite el certificado de facturación electrónica?",
    a: "PahCertificar facilita la solicitud. Una entidad de certificación digital acreditada evalúa el expediente y, si lo aprueba, emite el certificado.",
    tag: "DIAN",
  },
  {
    q: "¿Cómo compro una firma digital con token virtual?",
    a: "Elige el tipo y la vigencia en PahCertificar. Desde aquí continuarás al canal seguro de compra; nuestro equipo te acompaña en el seguimiento y custodia los archivos que se incorporen a tu cuenta.",
    tag: "Token virtual",
  },
  {
    q: "¿Puedo renovar antes del vencimiento?",
    a: "Puedes iniciar una nueva solicitud antes del vencimiento. La disponibilidad, los requisitos y los tiempos deben confirmarse para cada caso.",
    tag: "Renovación",
  },
  {
    q: "Olvidé la contraseña del archivo PFX",
    a: "Cuando la contraseña forma parte de tu expediente, se conserva cifrada y se entrega únicamente por canales autorizados. Si no está disponible, escríbenos para revisar las opciones aplicables.",
    tag: "Seguridad",
  },
  {
    q: "¿Cómo instalo el certificado en mi software?",
    a: "El archivo .pfx se puede importar en Windows (Certificados personales), macOS (Llavero), navegador y la mayoría de soluciones de firma. Tenemos guías paso a paso.",
    tag: "Instalación",
  },
];

const FAQ_EN: FAQ[] = [
  {
    q: "How long does the request take?",
    a: "We do not promise a fixed timeline. It depends on a complete file and the certification entity's review.",
    tag: "Timing",
  },
  {
    q: "What documents do I need?",
    a: "For individuals: the government ID is required and the RUT is optional. For legal entities: Chamber of Commerce, legal representative's ID and company RUT.",
    tag: "Documents",
  },
  {
    q: "Who issues the e-invoicing certificate?",
    a: "PahCertificar facilitates the request. An accredited digital certification entity reviews the file and issues the certificate if approved.",
    tag: "DIAN",
  },
  {
    q: "How do I buy a virtual-token digital signature?",
    a: "Choose the type and validity period in PahCertificar. You will continue from here to the secure purchase channel; our team supports the follow-up and stores the files added to your account.",
    tag: "Virtual token",
  },
  {
    q: "Can I renew before expiry?",
    a: "You may start a new request before expiry. Availability, requirements, and timing must be confirmed for each case.",
    tag: "Renewal",
  },
  {
    q: "I forgot my PFX password",
    a: "When the password is part of your request file, it is encrypted and delivered only through authorized channels. If it is unavailable, contact us to review the applicable options.",
    tag: "Security",
  },
  {
    q: "How do I install the certificate?",
    a: "The .pfx file can be imported into Windows (Personal certificates), macOS (Keychain), your browser, and most signing software. We have step-by-step guides.",
    tag: "Install",
  },
];

export function Help() {
  const { t, locale } = useT();
  const [q, setQ] = useState("");

  const faqs = locale === "es" ? FAQ_ES : FAQ_EN;
  const filtered = useMemo(
    () =>
      faqs.filter(
        (f) =>
          !q ||
          f.q.toLowerCase().includes(q.toLowerCase()) ||
          f.a.toLowerCase().includes(q.toLowerCase()) ||
          f.tag.toLowerCase().includes(q.toLowerCase())
      ),
    [faqs, q]
  );

  return (
    <div className="space-y-8">
      <SectionHeading title={t("help.title")} subtitle={t("help.subtitle")} />

      <Card className="p-4 sm:p-6">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            className="input pl-9"
            placeholder={t("help.search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
      </Card>

      <div className="grid gap-6 md:grid-cols-[1.3fr_1fr]">
        <div className="space-y-3">
          {filtered.map((f, i) => (
            <FAQItem key={i} q={f} />
          ))}
        </div>

        <Card className="space-y-5 p-6 h-fit md:sticky md:top-20">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{t("help.contact_title")}</h3>
            <p className="mt-1 text-sm text-slate-600">{t("help.contact_body")}</p>
          </div>
          <div className="flex flex-col gap-3">
            <Button onClick={() => (window.location.href = "mailto:hola@pahcertificar.com")}>
              <Mail className="h-4 w-4" />
              {t("help.contact_email")}
            </Button>
            <Button
              variant="secondary"
              onClick={() => window.open("https://wa.me/573024638964", "_blank", "noreferrer")}
            >
              <MessageSquare className="h-4 w-4" />
              {t("help.contact_whatsapp")}
            </Button>
          </div>
          <div className="rounded-xl bg-slate-50 p-4 text-xs text-slate-600">
            Lunes a viernes: 8:00 – 18:00 · Sábados: 9:00 – 13:00
          </div>
        </Card>
      </div>
    </div>
  );
}

function FAQItem({ q }: { q: FAQ }) {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-4 p-5 text-left"
        aria-expanded={open}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="tag bg-brand-50 text-brand-700">{q.tag}</span>
          </div>
          <p className="mt-2 font-medium text-slate-900">{q.q}</p>
        </div>
        <ChevronDown className={cn("h-4 w-4 flex-none text-slate-400 transition", open && "rotate-180")} />
      </button>
      {open && <p className="border-t border-slate-100 p-5 text-sm leading-relaxed text-slate-600">{q.a}</p>}
    </Card>
  );
}
